import { useEffect, useState } from "react";
import logger from "../logger";
import {
  createFormApi,
  getFormApi,
  getFormFieldsApi,
  getGraphJson,
} from "../apiService";
import {
  APPLICATION_ID,
  DEFAULT_MSGS,
  isCSVString,
  isGuid,
  LOCALIZATION_FILE_URL,
  mergeFieldsIntoForm,
  setTabTitle,
} from "../utilities";
import useErrorMessage from "./useErrorMessage";

const useGetForm = ({
  data,
  form,
  formEvents,
  setForm,
  setFormEvents,
  graphId,
  filePath,
}) => {
  const [graphJSON, setGraphJSON] = useState({
    DefaultMsgs: DEFAULT_MSGS,
    Events: [],
    Groups: [],
  });
  const [subscriptionData, setSubscriptionData] = useState(null);
  
  const displayUserFriendlyErrorMessage = useErrorMessage();

  const getCSVFile = (filePath) => {
    if (filePath) {
      return fetch(filePath).then((response) => {
        return response.text();
      });
    }
  };

  const getFormWithFields = ({ id }) => {
    getFormApi({ id })
      .then((response) => {
        const { data: form } = response;
        setTabTitle(form?.title);
        getFormFieldsApi({ id })
          .then((response) => {
            const { events, accepting, phaseNo } = response.data;
            try {
              getCSVFile(filePath)
                .then((file) => {
                  if (!isCSVString(file)) {
                    const formWithFields = mergeFieldsIntoForm({
                      form,
                      events,
                      phaseNo,
                      graphLocalizedJSON: graphJSON,
                    });
                    setForm({ id, fetching: false, ...formWithFields });
                    setFormEvents(events);
                    return;
                  }
                  getGraphJson(LOCALIZATION_FILE_URL, { graphId, file })
                    .then(({ data: { data, success } }) => {
                      if (success) {
                        const formWithFields = mergeFieldsIntoForm({
                          form,
                          events,
                          accepting,
                          phaseNo,
                          graphLocalizedJSON: data,
                        });
                        setForm({ id, ...formWithFields });
                        setFormEvents(events);
                        setGraphJSON(data);
                      } else {
                        const formWithFields = mergeFieldsIntoForm({
                          form,
                          events,
                          accepting,
                          phaseNo,
                          graphLocalizedJSON: graphJSON,
                        });
                        setForm({ id, ...formWithFields });
                        setFormEvents(events);
                      }
                    })
                    .catch((error) => {
                      const formWithFields = mergeFieldsIntoForm({
                        form,
                        events,
                        accepting,
                        phaseNo,
                        graphLocalizedJSON: graphJSON,
                      });
                      setForm({ id, ...formWithFields });
                      setFormEvents(events);
                      console.log("getGraphJson err..........", error);
                      logger.error({
                        message: "graph json not fetched.",
                        module: "Formserver",
                        fileName: "useGetForm.js",
                        functionName: "getGraphJson",
                        logLevel: "Critical",
                        details: error,
                        parameters: JSON.stringify({ graphId, file }),
                        url: window.location.href,
                        application: APPLICATION_ID, //TODO: for formserver we need application id
                      });
                    });
                })
                .catch((error) => {
                  const formWithFields = mergeFieldsIntoForm({
                    form,
                    events,
                    accepting,
                    phaseNo,
                    graphLocalizedJSON: graphJSON,
                  });
                  setForm({ id, fetching: false, ...formWithFields });
                  setFormEvents(events);
                  logger.error({
                    message: "csv file not found.",
                    module: "Formserver",
                    fileName: "useGetForm.js",
                    functionName: "getCSVFile",
                    logLevel: "Critical",
                    details: error,
                    parameters: JSON.stringify({ graphId }),
                    url: window.location.href,
                    application: APPLICATION_ID, //TODO: for formserver we need application id
                  });
                });
            } catch (error) {
              const formWithFields = mergeFieldsIntoForm({
                form,
                events,
                accepting,
                phaseNo,
                graphLocalizedJSON: graphJSON,
              });
              setForm({ id, fetching: false, ...formWithFields });
              setFormEvents(events);
              
              //HINT: right now disabled the csv logging because client don't want to see it 

              /* logger.info({
                message: "graph csv is not exist.",
                module: "Formserver",
                fileName: "useGetForm.js",
                functionName: "getGraphJson",
                logLevel: "Critical",
                details: error,
                parameters: JSON.stringify({ graphId }),
                url: window.location.href,
                application: APPLICATION_ID, //TODO: for formserver we need application id
              }); */
            }
          })
          .catch((error) => {
            setForm({ ...form, id, fetching: false, error });
            logger.error({
              message: error,
              module: "Formserver",
              fileName: "useGetForm.js",
              functionName: "getFormFieldsApi",
              logLevel: "Critical",
              details: error,
              parameters: JSON.stringify({ id }),
              url: window.location.href,
              application: APPLICATION_ID, //TODO: for formserver we need application id
            });
            displayUserFriendlyErrorMessage(error, `The GUID ${id} was unknown to the DCR.Form backend.`);
          });
      })
      .catch((error) => {
        setForm({ ...form, fetching: false, error });
        logger.error({
          message: error,
          module: "Formserver",
          fileName: "useGetForm.js",
          functionName: "getFormApi",
          logLevel: "Critical",
          details: error,
          parameters: JSON.stringify({ id }),
          url: window.location.href,
          application: APPLICATION_ID, //TODO: for formserver we need application id
        });
        displayUserFriendlyErrorMessage(error, `The GUID ${id} was unknown to the DCR.Form backend.`);
      });
  };

  useEffect(() => {
    if (data) {
      setForm({ ...form, fetching: true });
      createFormApi({ data })
        .then(({ data }) => {
          getFormWithFields({ id: data.dcrToken });
          setSubscriptionData(data.subscriptionData);
        })
        .catch((error) => {
          setForm({ ...form, fetching: false, error });
          logger.error({
            message: error,
            module: "Formserver",
            fileName: "useGetForm.js",
            functionName: "createFormApi",
            logLevel: "Critical",
            details: error,
            parameters: JSON.stringify({ data }),
            url: window.location.href,
            application: APPLICATION_ID, //TODO: for formserver we need application id
          });
          displayUserFriendlyErrorMessage(error, isGuid(data) ? `The GUID ${data} was unknown to the DCR.Form backend.` : "We didn't found the form you are looking for. Please try again.");
        });
    }
  }, [data]);

  return { form, formEvents, graphJSON, subscription: subscriptionData };
};

export default useGetForm;
